import React from 'react';
import { ViewPager } from 'components/view-pager';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import { ControlsBox, PagerWrapper } from 'components/unit-page/galleryABTests/styled';
import { shouldLazilyLoad } from 'utils/gallery';
import { ControlsArrow } from 'components/unit-page/galleryABTests/GalleryPager';
import { Pager } from 'components/cards/listing/Pager';
import * as Styled from './styled';
import { ImageInfo } from '../ImageInfo';
import { Images } from '../../sections/gallery';
import { NavigateToFn, Route } from 'config/routes';
import { connect } from 'react-redux';
import { navigateTo } from 'store/state/router/actions';
import { getAgentOfficeCategory } from 'screens/AgentOfficePages/helper';
import { useRoute } from 'react-router5';

interface GalleryProps {
  images: Images[];
  slideIndex: number;
  setSlideIndex: (index: number) => void;
  onPrevSlide: () => void;
  onNextSlide: () => void;
  navigate?: NavigateToFn;
}

const LAZY_LOAD_MARGIN = 3;
const IMAGE_PIXEL_DENSITY = 2;

const SliderItem: React.FC<{ url: string; shouldLoad: boolean; clickHandler: () => void }> = ({
  url,
  shouldLoad,
  clickHandler,
}) => {
  const breakpointValue = useScreenBreakpoint();
  const constructImageURI = useImageUrlBuilder();
  const height = Styled.rangeToGalleryHeight[breakpointValue];
  const uri = constructImageURI(url, { height: height * IMAGE_PIXEL_DENSITY });

  return (
    <Styled.SliderItemContainer onClick={clickHandler}>
      <div
        data-auto="developer-image"
        data-auto-url={uri}
        style={{
          backgroundImage: shouldLoad ? `url("${uri}")` : null,
        }}
      />
    </Styled.SliderItemContainer>
  );
};

const Gallery: React.FC<GalleryProps> = ({ images, slideIndex, onNextSlide, onPrevSlide, setSlideIndex, navigate }) => {
  const imageInfo = images.length ? <ImageInfo {...images[slideIndex]} /> : null;
  const { route } = useRoute();
  const isOfficePage = route.name === Route.OfficePage;

  const handleClick = (r: Route, id: string) => {
    navigate(r, { id, tracking_event_source: `${getAgentOfficeCategory(isOfficePage)}_page_gallery` });
  };

  return (
    <div>
      <Styled.GalleryWrapper>
        <ViewPager
          align="center"
          slideIndex={slideIndex}
          onSlideIndexChange={setSlideIndex}
        >
          {images.map(({ img, bulletinType, id }, i) => {
            const shouldLoad = shouldLazilyLoad(slideIndex, i, images.length, LAZY_LOAD_MARGIN);
            return (
              <SliderItem
                key={`${img}-${i}`}
                url={img}
                shouldLoad={shouldLoad}
                clickHandler={() => handleClick(bulletinType === 'bulletin' ? Route.UnitPage : Route.UnitPageCommercial, id)}
              />
            );
          })}
        </ViewPager>
        {images.length > 1 ? (
          <ShowAt from={3}>
            <ControlsBox>
              <ControlsArrow dir="prev" onClick={onPrevSlide}/>
              <ControlsArrow dir="next" onClick={onNextSlide}/>
            </ControlsBox>
          </ShowAt>
        ) : null}
        <PagerWrapper>
          <Pager
            total={images.length}
            onChangeIndex={setSlideIndex}
            index={slideIndex}
            limit={5}
          />
        </PagerWrapper>
      </Styled.GalleryWrapper>
      <ShowAt at={1}>
        {imageInfo}
      </ShowAt>
    </div>
  );
};

const mapDispatchToProps = {
  navigate: navigateTo,
};

export default connect(null, mapDispatchToProps)(Gallery);
